import { useState, useEffect } from 'react';
import { Download, Plus, TrendingUp } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useOtherIncomes } from '@/lib/queries';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { fmtMoney, todayISO } from '@/lib/format';
import { buildCsv, downloadCsv } from '@/lib/csv';
import { PageHeader } from '@/components/PageHeader';

export const OtherIncomesPage = () => {
  const { data: items = [] } = useOtherIncomes();
  const total = items.reduce((s: number, e: any) => s + Number(e.amount), 0);

  return (
    <div className="animate-in-up space-y-4">
      <PageHeader
        title="Прочие доходы"
        subtitle={items.length ? `${items.length} операций` : undefined}
        right={
          <div className="flex items-center gap-2">
            {items.length ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  downloadCsv(
                    `chernova-other-incomes_${todayISO()}`,
                    buildCsv(items, [
                      { header: 'Дата', value: (e: any) => String(e.date).slice(0, 10) },
                      { header: 'Наименование', value: (e: any) => e.title },
                      { header: 'Сумма', value: (e: any) => Number(e.amount) },
                    ]),
                  )
                }
              >
                <Download className="h-4 w-4" />
              </Button>
            ) : null}
            <NewIncomeDialog />
          </div>
        }
      />

      {!!items.length && (
        <Card>
          <CardContent className="space-y-1 p-3">
            <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Всего прочих доходов
            </div>
            <div className="text-2xl font-bold tabular text-success">{fmtMoney(total)}</div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {items.map((e: any) => (
          <IncomeRow key={e.id} income={e} />
        ))}
        {!items.length && (
          <Card>
            <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-success-soft text-success">
                <TrendingUp className="h-6 w-6" />
              </div>
              <div>
                <div className="font-semibold">Записей нет</div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Добавьте продажу косметики или другой доход
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

    </div>
  );
};

const IncomeRow = ({ income: e }: { income: any }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Card
        className="cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-soft-md active:scale-[0.99]"
        onClick={() => setOpen(true)}
      >
        <CardContent className="flex items-center gap-3 p-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-success-soft text-success">
            <TrendingUp className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate font-semibold">{e.title}</div>
            <div className="text-xs text-muted-foreground">
              {new Date(e.date).toLocaleDateString('ru-RU')}
            </div>
          </div>
          <div className="text-base font-bold tabular text-success">
            {fmtMoney(Number(e.amount))}
          </div>
        </CardContent>
      </Card>
      <IncomeDetailsDialog income={e} open={open} onClose={() => setOpen(false)} />
    </>
  );
};

const IncomeDetailsDialog = ({
  income,
  open,
  onClose,
}: {
  income: any;
  open: boolean;
  onClose: () => void;
}) => {
  const qc = useQueryClient();
  const [title, setTitle] = useState(income.title);
  const [date, setDate] = useState(String(income.date).slice(0, 10));
  const [amount, setAmount] = useState(Number(income.amount));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setTitle(income.title);
      setDate(String(income.date).slice(0, 10));
      setAmount(Number(income.amount));
      setError(null);
    }
  }, [open, income]);

  const reset = () => {
    setTitle(income.title);
    setDate(String(income.date).slice(0, 10));
    setAmount(Number(income.amount));
    setError(null);
  };

  const save = async () => {
    if (submitting) return;
    setError(null);
    if (!title.trim()) {
      setError('Укажите наименование');
      return;
    }
    if (!amount || amount <= 0) {
      setError('Укажите сумму');
      return;
    }
    setSubmitting(true);
    try {
      await api.patch(`/other-incomes/${income.id}`, {
        title: title.trim(),
        date,
        amount,
      });
      qc.invalidateQueries({ queryKey: ['other_incomes'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Не удалось сохранить');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Редактировать доход</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Наименование</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div>
            <Label>Дата</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div>
            <Label>Сумма (₽)</Label>
            <Input
              type="number"
              step="0.01"
              value={amount || ''}
              onChange={(e) => setAmount(Number(e.target.value))}
            />
          </div>
          {error && <div className="text-xs text-destructive">{error}</div>}
          <Button className="w-full" size="lg" onClick={save} disabled={submitting}>
            Сохранить
          </Button>
          <Button
            variant="destructive"
            className="w-full"
            onClick={async () => {
              try {
                await api.delete(`/other-incomes/${income.id}`);
                qc.invalidateQueries({ queryKey: ['other_incomes'] });
                qc.invalidateQueries({ queryKey: ['stats'] });
                onClose();
              } catch (e: any) {
                alert(e?.response?.data?.error ?? 'Не удалось удалить');
              }
            }}
          >
            Удалить
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const NewIncomeDialog = () => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(todayISO());
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState<number>(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();

  const submit = async () => {
    if (submitting) return;
    setError(null);
    if (!title.trim()) {
      setError('Укажите наименование');
      return;
    }
    if (!amount || amount <= 0) {
      setError('Укажите сумму');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/other-incomes', {
        date,
        title: title.trim(),
        amount,
      });
      qc.invalidateQueries({ queryKey: ['other_incomes'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
      setOpen(false);
      setTitle('');
      setAmount(0);
      setDate(todayISO());
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Ошибка сохранения');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="icon-sm" aria-label="Новый доход">
          <Plus className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Новый прочий доход</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Наименование</Label>
            <Input
              placeholder="Например: шампунь, масло для волос"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div>
            <Label>Дата продажи</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div>
            <Label>Сумма (₽)</Label>
            <Input
              type="number"
              step="0.01"
              value={amount || ''}
              onChange={(e) => setAmount(Number(e.target.value))}
            />
          </div>
          {error && <div className="text-xs text-destructive">{error}</div>}
          <Button
            onClick={submit}
            disabled={submitting || !title.trim() || !amount}
            size="lg"
            className="w-full"
          >
            <Plus className="h-4 w-4" /> Сохранить
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
